import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { includes, some } from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { usePostHog } from 'posthog-js/react';
import { OrderData, OrderPageData, OrderService, OrderStatus, PaymentFormType, PaymentsService } from '../api';

import RotatingLoader from '../common/ui/RotatingLoader';
import { useAccountData } from '../order_page/AccountSpecificThemeWrapper';
import { OrderPageTypography } from '../order_page/themedComponents/OrderPageTypography';
import { OrderPageIntent } from '../order_page/OrderPageIntent';
import { OrderFinalDetailsTable } from '../order_page/steps/OrderFinalDetailsTable';
import { getOrderPage } from '../common/AccountUtils';
import PaymentIframe from '../order_page/steps/PaymentIframe';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { OrderPageButton } from '../order_page/themedComponents/OrderPageButton';
import { getDateFormat } from '../common/getDateAndTimeFormat';

export const CompleteRequestedOrder = () => {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const account = useAccountData();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderid');
  const { longDateFormat } = getDateFormat(account);

  const [order, setOrder] = useState<OrderData | undefined>(undefined);
  const [orderPage, setOrderPageData] = useState<OrderPageData | undefined>(undefined);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDateAvailable, setIsDateAvailable] = useState(true);

  useEffect(() => {
    const initPage = async () => {
      try {
        if (!orderId) {
          setError('No order ID provided');
          return;
        }
        posthog.capture('Completing order', { orderId });

        const orderData = await OrderService.getOrder(orderId);

        if (orderData.status !== OrderStatus.REQUESTED) {
          setError('This order cannot be completed');
          return;
        }

        setOrder(orderData);
        setOrderPageData(getOrderPage(account, orderData.order_page_id));

        const paymentUrl = await PaymentsService.getPaymentForm({
          account_id: orderData.account_id,
          experience_id: orderData.experience_id,
          order_page_id: orderData.order_page_id,
          entity_id: orderData.id,
          language: orderData.language,
          payment_form_type: PaymentFormType.ORDER,
        });

        setPaymentUrl(paymentUrl);
      } catch (e) {
        setError('Failed to load order details');
      } finally {
        setIsLoading(false);
      }
    };

    void initPage();
  }, [orderId]);
  useEffect(() => {
    const checkAvailability = async () => {
      if (!order) {
        return;
      }

      const orderDetails = {
        persons: order.persons,
        duration: order.duration,
        price: order.price,
        rooms: order.rooms,
        custom_details: order.custom_details,
      };

      try {
        const response = await OrderService.getAvailableDates({
          account_id: order.account_id,
          order_page_id: order.order_page_id,
          order_details: orderDetails,
        });

        const isStillAvailable = response.some(
          (dateObj) =>
            dateObj.date === order.date && dateObj.times.some((t) => t.seconds === order.time && !t.isDisabled),
        );

        setIsDateAvailable(isStillAvailable);

        if (!isStillAvailable) {
          setError(t('order_page.date_not_available', { date: moment(order.date).format(longDateFormat) }));
        }
      } catch (e) {
        console.error('Failed to check date availability:', e);
      }
    };

    void checkAvailability();
  }, [order]);

  if (isLoading) {
    return <RotatingLoader />;
  }

  const shouldShowSandbox =
    paymentUrl && some(['sandbox', 'paymentsdev', 'sc_test', 'elibis'], (substring) => includes(paymentUrl, substring));
  let stripe_integration_publishable_key;

  if (account?.stripe_connect) {
    stripe_integration_publishable_key = account?.stripe_connect.is_stripe_test_account
      ? import.meta.env.VITE_REACT_APP_TEST_STRIPE_PUBLISHABLE_KEY
      : import.meta.env.VITE_REACT_APP_STRIPE_PUBLISHABLE_KEY;
  }
  if (!orderPage || !order) {
    return null;
  }
  return (
    <Grid>
      <Grid
        mb={2}
        container
        display="flex"
        justifyContent="space-around"
        sx={{ '::-webkit-scrollbar': { display: 'none' } }}
        style={{
          height: '100%',
          width: '100%',
        }}>
        <Grid
          container
          flexDirection="column"
          style={{
            borderRadius: '12px',
            background: theme.customTheme.palette.background,
            height: '100%',
            width: '100%',
            maxWidth: '710px',
          }}
          className="order-page-wrapper">
          <Grid
            px={isSmallScreen ? undefined : 4}
            container
            flexDirection="column"
            style={{
              color: theme.customTheme.palette.text.primary,
              border: `2px solid ${theme.customTheme.palette.border}`,
              borderRadius: '12px',
            }}
            boxShadow={4}
            py={2}>
            {error ? (
              <Grid container justifyContent="center" alignItems="center" p={3} direction="column" spacing={2}>
                <Grid container justifyContent="center">
                  <OrderPageTypography variant="h1" style={{ textAlign: 'center', width: '100%' }}>
                    {orderPage.label?.[order.language as 'en' | 'he']}
                  </OrderPageTypography>
                </Grid>
                <Grid item>
                  <Typography color="error" variant={'h5'}>
                    {error}
                  </Typography>
                </Grid>
                {!isDateAvailable && (
                  <Grid item>
                    <OrderPageButton
                      variant="contained"
                      onClick={() =>
                        navigate(`/order/${order?.account_id}/${order?.experience_id}/${order?.order_page_id}`)
                      }>
                      <OrderPageTypography variant="h6" color="button.contained" bold>
                        {t('order_page.chooseNewDate')}
                      </OrderPageTypography>
                    </OrderPageButton>
                  </Grid>
                )}
              </Grid>
            ) : (
              <>
                <Box
                  sx={{ justifyContent: 'flex-start', display: 'flex' }}
                  onClick={() => setIsResetDialogOpen(true)}
                  mb={2}
                  px={isSmallScreen ? 2 : 0}>
                  <OrderPageTypography
                    style={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      color: 'text.secondary',
                    }}>
                    {t('reset')}
                  </OrderPageTypography>
                </Box>
                <Grid container justifyContent="center">
                  <OrderPageTypography variant="h1" style={{ textAlign: 'center', width: '100%' }}>
                    {orderPage.label?.[order.language as 'en' | 'he']}
                  </OrderPageTypography>
                </Grid>
                <OrderFinalDetailsTable
                  orderPageIntent={OrderPageIntent.BUY_NOW}
                  shouldShowDuration={orderPage.show_duration}
                  account={account!}
                  orderPageState={{
                    dateAndTimeDetails: {
                      date: order.date!,
                      time: order.time,
                    },
                    details: {
                      persons: order.persons,
                      duration: order.duration,
                      custom_details: order.custom_details,
                    },
                    orderPage,
                  }}
                />
                {shouldShowSandbox && <OrderPageTypography color="error">SANDBOX</OrderPageTypography>}
                <Grid item px={1}>
                  <PaymentIframe
                    paymentUrl={paymentUrl}
                    stripe_integration_publishable_key={stripe_integration_publishable_key}
                    paymentIframeSettings={account?.payment_integration_settings?.payment_iframe_settings}
                    stripeAccountId={account?.stripe_connect?.stripe_account_id}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <ConfirmationDialog
            title={t('order_page.reset_dialog.title')}
            ctaText={t('reset')}
            isOpen={isResetDialogOpen}
            onClose={(actionConfirmed: any) => {
              setIsResetDialogOpen(false);
              if (actionConfirmed) {
                navigate(`/order/${order?.account_id}/${order?.experience_id}/${order?.order_page_id}`);
              }
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
